/**
 * Image helper utilities for safe image source handling
 */

// Allowed hostnames for remote images
const ALLOWED_HOSTNAMES = ['nbtffxcscxqonrabxcdp.supabase.co']

// Allowed path pattern for Supabase public storage
const ALLOWED_PATH = /^\/storage\/v1\/object\/public\/projects\/.+/

/**
 * Safely processes image URLs by validating hostname and decoding URI components
 * @param rawSrc - Raw image source URL that may need decoding/validation
 * @returns Valid image source URL or null if invalid
 */
export function safeImageSrc(rawSrc?: string | null): string | null {
  if (!rawSrc) {
    return null
  }

  try {
    // Decode URI components once
    const decoded = decodeURIComponent(rawSrc)
    
    // Parse URL to validate hostname and pathname
    const u = new URL(decoded)
    
    // Check if hostname is in allowed list
    if (!ALLOWED_HOSTNAMES.includes(u.hostname)) {
      // TODO: Remove console.warn after 1-2 deploys
      console.warn(`Image hostname not allowed: ${u.hostname}`)
      return null
    }
    
    // Check if pathname matches allowed pattern
    if (!ALLOWED_PATH.test(u.pathname)) {
      // TODO: Remove console.warn after 1-2 deploys
      console.warn(`Image path not allowed: ${u.pathname}`)
      return null
    }
    
    return decoded
  } catch (error) {
    // TODO: Remove console.warn after 1-2 deploys
    console.warn(`Invalid image URL: ${rawSrc}`, error)
    return null
  }
}

/**
 * Temporary function to log image errors for debugging
 * @param originalSrc - The original source that failed
 * @param finalSrc - The final processed source that failed
 */
export function logImageError(originalSrc: string | null | undefined, finalSrc: string | null | undefined) {
  console.error('Image failed to load:', {
    originalSrc,
    finalSrc,
    timestamp: new Date().toISOString()
  })
}

// For CommonJS compatibility (for tests)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { safeImageSrc, logImageError }
}